import React from 'react';
import { SelectField, Text } from '@aws-amplify/ui-react';
import { api3Get } from '../api/api3Client';

function segmentLabel(segment) {
  const index = segment?.segmentIndex ?? segment?.segment_index;
  const start = segment?.startString ?? segment?.start_string ?? '';
  const end = segment?.endString ?? segment?.end_string ?? '';
  return `Contract ${index}: ${start} → ${end}`;
}

export function useRentalSegments(resId) {
  const [segments, setSegments] = React.useState([]);
  const [selectedLedgerVersion, setSelectedLedgerVersion] = React.useState('');
  const [loadError, setLoadError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    async function loadSegments() {
      if (!resId) {
        setSegments([]);
        setSelectedLedgerVersion('');
        return;
      }
      setIsLoading(true);
      setLoadError('');
      try {
        const res = await api3Get(`/reservations/${encodeURIComponent(resId)}/rental-segments`);
        if (cancelled) {
          return;
        }
        const rows = Array.isArray(res?.segments) ? res.segments : [];
        setSegments(rows);
        if (rows.length > 0) {
          const latest = rows[rows.length - 1];
          setSelectedLedgerVersion(String(latest.ledgerVersion ?? latest.ledger_version ?? ''));
        } else {
          setSelectedLedgerVersion('');
        }
      } catch (e) {
        if (!cancelled) {
          setSegments([]);
          setSelectedLedgerVersion('');
          setLoadError(e?.message || 'Unable to load rental contracts');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }
    loadSegments();
    return () => {
      cancelled = true;
    };
  }, [resId]);

  return {
    segments,
    selectedLedgerVersion,
    setSelectedLedgerVersion,
    loadError,
    isLoading,
    hasMultipleSegments: segments.length > 1,
  };
}

export default function RentalSegmentPicker({
  segments,
  selectedLedgerVersion,
  onChange,
  loadError,
}) {
  if (loadError) {
    return <Text>{loadError}</Text>;
  }
  if (!segments || segments.length <= 1) {
    return null;
  }
  return (
    <SelectField
      label="Rental contract"
      descriptiveText="Each paid extension is a separate rental contract."
      value={selectedLedgerVersion}
      onChange={(e) => onChange(e.target.value)}
    >
      {segments.map((segment) => {
        const version = String(segment.ledgerVersion ?? segment.ledger_version ?? '');
        return (
          <option key={version} value={version}>
            {segmentLabel(segment)}
          </option>
        );
      })}
    </SelectField>
  );
}
